import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, LockIcon, WalletIcon, AiIcon } from '../components/Icons.js';
import heroIllustration from '../assets/hero-illustration.svg';

export function Landing() {
  const navigate = useNavigate();

  useEffect(() => { document.title = 'CryptoGuardians \u2014 Institutional-Grade Crypto Security'; }, []);

  return (
    <>
      {/* ── SECTION 1: Hero ── */}
      <section className="hero">
        <div className="container">
          <div className="hero-layout">
            <div className="hero-content">
              <h1>Institutional-Grade Crypto Security for Your Wallet</h1>
              <p className="hero-subtitle">
                CryptoGuardians protects your wallet from malicious smart contracts, wallet drainers,
                and hidden threats before your funds are at risk.
              </p>
              <p className="lp-trust-indicator">
                Trusted by security-conscious crypto investors worldwide.
              </p>
            </div>
            <div className="hero-illustration">
              <img
                src={heroIllustration}
                alt="CryptoGuardians security protection"
                className="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: Problem ── */}
      <section className="features" id="features">
        <div className="container">
          <h2>Crypto Threats Are Increasing &mdash; Most Wallets Are Unprotected</h2>
          <div className="lp-problem-body">
            <p>
              Every day, investors unknowingly interact with malicious contracts that drain wallets instantly.
            </p>
            <p>
              Traditional wallets cannot detect many of these threats before transactions are signed.
            </p>
            <p className="lp-problem-solution">
              CryptoGuardians identifies threats before damage occurs.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: Solution ── */}
      <section className="snap-explainer">
        <div className="container">
          <h2>Real-Time Threat Intelligence</h2>
          <p className="section-subtitle">
            Our proprietary Crypto Intel Engine analyzes smart contracts, deployers, and on-chain behavior
            in real time to detect threats before funds are at risk.
          </p>
          <div className="features-grid" style={{ marginTop: '2rem' }}>
            <div className="card feature-card">
              <ShieldCheckIcon className="feature-svg-icon" aria-hidden="true" />
              <h3>Smart Contract Risk Analysis</h3>
              <p>Deep analysis of contract code, permissions, and hidden functions before you interact.</p>
            </div>
            <div className="card feature-card">
              <AiIcon className="feature-svg-icon" aria-hidden="true" />
              <h3>Wallet Behavior Monitoring</h3>
              <p>Track on-chain patterns and detect suspicious activity targeting your assets.</p>
            </div>
            <div className="card feature-card">
              <WalletIcon className="feature-svg-icon" aria-hidden="true" />
              <h3>Deployer Reputation Analysis</h3>
              <p>Identify malicious deployers and their network of associated contracts.</p>
            </div>
            <div className="card feature-card">
              <LockIcon className="feature-svg-icon" aria-hidden="true" />
              <h3>Cross-Chain Threat Intelligence</h3>
              <p>Protection across Ethereum, Solana, Base, BNB Chain, and Avalanche.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: Snap ── */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <h2>Optional Real-Time Protection with MetaMask Snap</h2>
          <p className="section-subtitle">
            Install the CryptoGuardians Snap to receive real-time protection directly inside your wallet.
            Every transaction is analyzed before you sign.
          </p>
          <div className="steps-grid" style={{ marginTop: '2rem' }}>
            <div className="step-card card">
              <div className="step-number">1</div>
              <h3>Connect Wallet</h3>
              <p>Sign in with your wallet to access the Security Dashboard.</p>
            </div>
            <div className="step-card card">
              <div className="step-number">2</div>
              <h3>Install Snap</h3>
              <p>Add the CryptoGuardians Snap to MetaMask for real-time protection.</p>
            </div>
            <div className="step-card card">
              <div className="step-number">3</div>
              <h3>Automatic Protection</h3>
              <p>Every transaction is scanned before you sign. No technical knowledge required.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              className="btn btn-secondary btn-lg"
              onClick={() => navigate('/wallet')}
            >
              Install Snap
            </button>
            <p className="lp-snap-note">
              Snap is an optional enhancement. Full security scanning is available without it.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: Authority ── */}
      <section className="features">
        <div className="container">
          <h2>Institutional-Level Security &mdash; Now Available to Everyone</h2>
          <div className="lp-authority-body">
            <p>
              CryptoGuardians provides the same level of threat intelligence used by security professionals
              and institutional investors.
            </p>
            <p>
              Our system continuously evaluates risk using multiple independent security layers
              to deliver earlier detection, higher accuracy, and greater protection.
            </p>
          </div>
          <div className="steps-grid" style={{ marginTop: '2rem' }}>
            <div className="step-card card">
              <h3>Earlier Detection</h3>
              <p>Identify threats before they reach your wallet.</p>
            </div>
            <div className="step-card card">
              <h3>Higher Accuracy</h3>
              <p>Multi-layer analysis reduces false positives.</p>
            </div>
            <div className="step-card card">
              <h3>Greater Protection</h3>
              <p>Comprehensive coverage across contracts, deployers, and behavior.</p>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
